import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import type { CSSProperties } from 'react';
import type React from 'react';

import classNames from 'classnames';
import { isEqual } from 'lodash';
import type { StageProps } from 'react-konva';
import { Stage } from 'react-konva';

import st from './AdaptiveStage.module.sass';
import { ANCHOR_PADDING } from './constants';
import type { TStageSize } from './types';

export interface AdaptiveStageProps extends StageProps {
  originSize?: TStageSize;
  onChangeSize?: (stageSize: TStageSize) => void;
}

interface TStageSizePropsDTO {
  style: CSSProperties;
  width: number;
  height: number;
  x: number;
  y: number;
}

function convertSizeToStageSizeProps(size: TStageSize) {
  return {
    width: size.width + ANCHOR_PADDING * 2,
    height: size.height + ANCHOR_PADDING * 2,
    style: {
      height: size.height + ANCHOR_PADDING * 2,
      width: size.width + ANCHOR_PADDING * 2,
    },
    x: ANCHOR_PADDING,
    y: ANCHOR_PADDING,
  } as TStageSizePropsDTO;
}

function AdaptiveStage({
  originSize = undefined,
  onChangeSize = undefined,
  ...props
}: AdaptiveStageProps): React.JSX.Element {
  const [rootSize, setRootSize] = useState<TStageSize | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rootSizeRef = useRef<TStageSize | undefined>(undefined);

  const stageSizePropsDTO: TStageSizePropsDTO | undefined = useMemo(() => {
    if (!rootSize) return undefined;
    if (!originSize) return convertSizeToStageSizeProps(rootSize);

    const { width: containerWidth, height: containerHeight } = rootSize;
    const { width: originWidth, height: originHeight } = originSize;

    const scale = Math.min(
      containerWidth / originWidth,
      containerHeight / originHeight,
    );

    const stageSizeProps: TStageSize = {
      width: originWidth * scale,
      height: originHeight * scale,
    };

    return convertSizeToStageSizeProps(stageSizeProps);
  }, [originSize, rootSize]);

  const updateRootSize = useCallback((node: HTMLDivElement) => {
    const newRootSize: TStageSize = {
      width: node.getBoundingClientRect().width,
      height: node.getBoundingClientRect().height,
    };
    if (isEqual(newRootSize, rootSizeRef.current)) return;

    rootSizeRef.current = newRootSize;
    setRootSize(newRootSize);
    onChangeSize?.(newRootSize);
  }, [onChangeSize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    // изменение css-размера не вызывает коллбэк ref, поэтому обновляем Konva отдельно
    const observer = new ResizeObserver(() => updateRootSize(container));
    observer.observe(container);
    updateRootSize(container);

    return () => observer.disconnect();
  }, [updateRootSize]);

  return (
    <div
      className={classNames(st.Container)}
      ref={containerRef}
    >
      <Stage
        className={classNames(props.className, st.Stage)}
        {...stageSizePropsDTO}
        {...props}
      />
    </div>
  );
}

export { AdaptiveStage };
